import { useMemo } from "react";
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Paper,
  Stack,
  Button,
  Alert,
  Chip,
  CircularProgress,
  ToggleButton,
} from "@mui/material";
import { Inventory2, Tv, Podcasts } from "@mui/icons-material";
import { InventoryRow, MonthTile, aggregateMonths } from "../api/inventory";

interface Props {
  // Which corpora the run targets. Multi-select — the month grid shows
  // the union of eligible months and each tile's count sums the
  // selected corpora present that month.
  contentTypes: string[];
  onToggleContentType: (ct: string) => void;
  selected: string[]; // "YYYY-MM"
  onToggle: (month: string) => void;
  onSelectAll: (months: string[]) => void;
  // Inventory is fetched at the wizard level (so the funnel above the
  // step content can reuse it). null = still loading; [] = empty.
  inventory: InventoryRow[] | null;
  inventoryError: string | null;
}

// Step 1 — pick corpora + months from the inventory.
// Only rollup-eligible months are listed (count + export pre-flight
// require a rollup). Grouped by year, descending so the most recent
// year leads. Each year has Select all/Clear; a global Select all/
// Clear all sits in the funnel header so power-users can grab the
// whole archive in one click.
//
// The funnel header at the top is the first node in the wizard's
// "narrowing funnel": we sum rollup_n_rows across selected months and
// show "N documents in M months" — the population that subsequent
// steps will whittle down (filter, search).

const fmt = (n: number) => n.toLocaleString();

const CT_LABEL: Record<string, string> = {
  broadcast: "Broadcast",
  podcast: "Podcast",
};

export function StepMonths({
  contentTypes,
  onToggleContentType,
  selected,
  onToggle,
  onSelectAll,
  inventory: rawInventory,
  inventoryError,
}: Props) {
  // The wizard-level fetch returns the full inventory (a row per
  // content_type × year × month, including rollup-less ones). Collapse
  // it to one tile per month across the selected corpora so "select
  // all" can't pick a month that would fail pre-flight, and the counts
  // reflect the union the export will actually cover.
  const tiles = useMemo(
    () => (rawInventory ? aggregateMonths(rawInventory, contentTypes) : null),
    [rawInventory, contentTypes],
  );
  const error = inventoryError;

  // Totals across the *selected* set, computed once per inventory/selection
  // change. The indexByKey lookup also gives us the by-year subtotal
  // we surface in each year header.
  const totals = useMemo(() => {
    if (!tiles) return null;
    const indexByKey = new Map<string, MonthTile>(
      tiles.map((t) => [`${t.year}-${t.month}` as string, t]),
    );
    let docs = 0;
    let bytes = 0;
    for (const m of selected) {
      const t = indexByKey.get(m);
      if (t) {
        docs += t.rollup_n_rows;
        bytes += t.rollup_size_bytes;
      }
    }
    return { docs, bytes, indexByKey };
  }, [tiles, selected]);

  // Corpus selector — multi-select, sits above the guards so the user
  // can toggle corpora even when the current set has no eligible
  // months. Month coverage is per corpus, so the reducer clears the
  // month selection whenever this set changes.
  const selector = (
    <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
      <ToggleButton
        value="broadcast"
        size="small"
        color="primary"
        selected={contentTypes.includes("broadcast")}
        onChange={() => onToggleContentType("broadcast")}
      >
        <Tv fontSize="small" sx={{ mr: 1 }} />
        Broadcast
      </ToggleButton>
      <ToggleButton
        value="podcast"
        size="small"
        color="primary"
        selected={contentTypes.includes("podcast")}
        onChange={() => onToggleContentType("podcast")}
      >
        <Podcasts fontSize="small" sx={{ mr: 1 }} />
        Podcast
      </ToggleButton>
    </Stack>
  );

  if (error)
    return (
      <Box>
        {selector}
        <Alert severity="error" sx={{ mt: 2 }}>
          Failed to load inventory: {error}
        </Alert>
      </Box>
    );
  if (!tiles || !totals)
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 2 }}>
        <CircularProgress size={20} />
        <Typography>Loading inventory…</Typography>
      </Box>
    );
  if (contentTypes.length === 0)
    return (
      <Box>
        {selector}
        <Alert severity="info" sx={{ mt: 2 }}>
          Pick at least one corpus to see its months.
        </Alert>
      </Box>
    );
  if (tiles.length === 0)
    return (
      <Box>
        {selector}
        <Alert severity="warning" sx={{ mt: 2 }}>
          No rollup-eligible months for the selected corpora. Onboard some
          months first, or toggle a different corpus.
        </Alert>
      </Box>
    );

  // Group by year (descending so the recent year leads).
  const byYear = new Map<string, MonthTile[]>();
  for (const t of tiles) {
    const list = byYear.get(t.year) ?? [];
    list.push(t);
    byYear.set(t.year, list);
  }
  const years = Array.from(byYear.keys()).sort().reverse();

  const allMonths = tiles.map((t) => `${t.year}-${t.month}`);
  const everythingSelected = selected.length === allMonths.length;
  // Only worth annotating each tile with its corpora when more than one
  // is in play — otherwise the chips are noise.
  const showCorpusChips = contentTypes.length > 1;

  return (
    <Box>
      {selector}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Pick the months you want to export. Only months with a rollup are
        listed — others would fail pre-flight.
        {showCorpusChips
          ? " A month exports whichever selected corpora have a rollup for it."
          : ""}
      </Typography>

      <Paper
        variant="outlined"
        sx={{
          p: 2,
          mb: 2,
          borderColor: selected.length > 0 ? "primary.main" : "divider",
        }}
      >
        <Stack direction="row" alignItems="center" gap={2} flexWrap="wrap">
          <Inventory2 color={selected.length > 0 ? "primary" : "disabled"} />
          <Box flexGrow={1} minWidth={200}>
            <Typography variant="overline" color="text.secondary">
              Documents to scan
            </Typography>
            <Typography variant="h5">
              {selected.length === 0 ? "—" : `${fmt(totals.docs)} documents`}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {selected.length === 0
                ? `Pick one or more of the ${allMonths.length} available months. Population will narrow at the Filter step.`
                : `Across ${selected.length} of ${allMonths.length} months · ${fmt(Math.round(totals.bytes / 1024 / 1024))} MB rollup`}
            </Typography>
          </Box>
          <Button
            variant={everythingSelected ? "outlined" : "contained"}
            onClick={() =>
              everythingSelected ? onSelectAll([]) : onSelectAll(allMonths)
            }
          >
            {everythingSelected ? "Clear all" : "Select all"}
          </Button>
        </Stack>
      </Paper>

      <Stack spacing={2}>
        {years.map((year) => {
          const yearTiles = byYear.get(year) ?? [];
          const monthsInYear = yearTiles.map((t) => `${t.year}-${t.month}`);
          const allYearSelected = monthsInYear.every((m) =>
            selected.includes(m),
          );
          // Per-year doc subtotal — running tally for the months the
          // user has actually checked, not the year's total population.
          const yearSelectedDocs = yearTiles
            .filter((t) => selected.includes(`${t.year}-${t.month}`))
            .reduce((s, t) => s + t.rollup_n_rows, 0);
          const yearTotalDocs = yearTiles.reduce(
            (s, t) => s + t.rollup_n_rows,
            0,
          );
          return (
            <Paper key={year} variant="outlined" sx={{ p: 2 }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 1 }}
              >
                <Box>
                  <Typography variant="h6">{year}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {yearSelectedDocs > 0
                      ? `${fmt(yearSelectedDocs)} of ${fmt(yearTotalDocs)} documents selected`
                      : `${fmt(yearTotalDocs)} documents available across ${monthsInYear.length} months`}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  onClick={() =>
                    onSelectAll(
                      allYearSelected
                        ? selected.filter((m) => !monthsInYear.includes(m))
                        : Array.from(new Set([...selected, ...monthsInYear])),
                    )
                  }
                >
                  {allYearSelected ? "Clear" : "Select all"}
                </Button>
              </Stack>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {monthsInYear.map((m) => {
                  const tile = totals.indexByKey.get(m);
                  const docs = tile?.rollup_n_rows ?? 0;
                  return (
                    <FormControlLabel
                      key={m}
                      control={
                        <Checkbox
                          checked={selected.includes(m)}
                          onChange={() => onToggle(m)}
                        />
                      }
                      label={
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.75,
                          }}
                        >
                          <Typography variant="body2" component="span">
                            {m}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            component="span"
                          >
                            ({fmt(docs)})
                          </Typography>
                          {showCorpusChips &&
                            (tile?.contentTypes ?? []).map((ct) => (
                              <Chip
                                key={ct}
                                size="small"
                                variant="outlined"
                                label={CT_LABEL[ct] ?? ct}
                                sx={{ height: 18, fontSize: "0.65rem" }}
                              />
                            ))}
                        </Box>
                      }
                    />
                  );
                })}
              </Box>
            </Paper>
          );
        })}
      </Stack>
    </Box>
  );
}
